/**
 * Fetch wrapper shared by the kiosk and admin apps.
 *  - always sends cookies (credentials: 'include')
 *  - sends the CSRF token on state-changing requests (fetched from /auth/csrf, refreshed on demand)
 *  - on 401 TOKEN_EXPIRED/AUTH_REQUIRED, does ONE single-flight refresh and retries
 *  - throws ApiError with { status, code, message, details }
 */
export class ApiError extends Error {
  constructor(status, error = {}) {
    super(error.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code ?? 'NETWORK_ERROR';
    this.details = error.details;
    this.requestId = error.requestId;
  }

  /** Field errors from VALIDATION_FAILED / CONFLICT responses: { 'owner.email': 'message' } */
  get fields() {
    return this.details?.fields ?? {};
  }
}

const SAFE = new Set(['GET', 'HEAD']);
const NO_REFRESH = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/csrf', '/auth/mfa', '/auth/pin-login'];

export function createApiClient({ baseUrl, onAuthLost }) {
  let csrf = null;
  let csrfPromise = null;
  let refreshing = null;

  async function fetchCsrf() {
    csrfPromise ??= fetch(`${baseUrl}/auth/csrf`, { credentials: 'include' })
      .then((r) => r.json())
      .then((b) => {
        csrf = b.csrfToken;
        return csrf;
      })
      .finally(() => {
        csrfPromise = null;
      });
    return csrfPromise;
  }

  async function send(method, path, body, headers) {
    if (!SAFE.has(method) && !csrf) await fetchCsrf();
    // Files (image uploads) are sent as raw bytes with their own content type.
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
    let res;
    try {
      res = await fetch(`${baseUrl}${path}`, {
        method,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(body !== undefined
            ? { 'Content-Type': isBlob ? body.type || 'application/octet-stream' : 'application/json' }
            : {}),
          ...(!SAFE.has(method) ? { 'X-CSRF-Token': csrf } : {}),
          ...headers,
        },
        body: body === undefined ? undefined : isBlob ? body : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, { code: 'NETWORK_ERROR', message: 'Cannot reach the server. Check your connection.' });
    }
    const data = res.status === 204 ? null : await res.json().catch(() => null);
    if (data?.csrfToken) csrf = data.csrfToken;
    return { res, data };
  }

  function refresh() {
    refreshing ??= send('POST', '/auth/refresh', undefined, {})
      .then(({ res }) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  async function request(method, path, body, { headers = {}, retried = false } = {}) {
    const { res, data } = await send(method, path, body, headers);
    if (res.ok) return data;
    const err = new ApiError(res.status, data?.error);
    if (!retried && res.status === 403 && err.code === 'CSRF_INVALID') {
      csrf = null;
      return request(method, path, body, { headers, retried: true });
    }
    const canRefresh = !NO_REFRESH.some((p) => path.startsWith(p));
    if (!retried && res.status === 401 && canRefresh && ['TOKEN_EXPIRED', 'AUTH_REQUIRED'].includes(err.code)) {
      if (await refresh()) return request(method, path, body, { headers, retried: true });
      onAuthLost?.(err);
    } else if (res.status === 401 && canRefresh && retried) {
      onAuthLost?.(err);
    } else if (res.status === 401 && ['SESSION_REVOKED', 'SESSION_EXPIRED', 'ACCOUNT_DISABLED'].includes(err.code)) {
      onAuthLost?.(err);
    }
    throw err;
  }

  /** GET a file (CSV / Excel / PDF export) as { blob, filename }, with the same refresh rules. */
  async function download(path, { retried = false } = {}) {
    let res;
    try {
      res = await fetch(`${baseUrl}${path}`, { credentials: 'include' });
    } catch {
      throw new ApiError(0, { code: 'NETWORK_ERROR', message: 'Cannot reach the server. Check your connection.' });
    }
    if (res.ok) {
      const filename = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'download';
      return { blob: await res.blob(), filename };
    }
    const data = await res.json().catch(() => null);
    const err = new ApiError(res.status, data?.error);
    if (!retried && res.status === 401 && ['TOKEN_EXPIRED', 'AUTH_REQUIRED'].includes(err.code) && (await refresh()))
      return download(path, { retried: true });
    throw err;
  }

  return {
    download,
    get: (path, opts) => request('GET', path, undefined, opts),
    post: (path, body, opts) => request('POST', path, body ?? {}, opts),
    put: (path, body, opts) => request('PUT', path, body ?? {}, opts),
    patch: (path, body, opts) => request('PATCH', path, body ?? {}, opts),
    delete: (path, opts) => request('DELETE', path, undefined, opts),
    refresh,
    resetCsrf: () => {
      csrf = null;
    },
  };
}

/** Hand a downloaded blob to the browser as a file. */
export function saveFile({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Read `#token=...` from the URL fragment (password links) and scrub it from history. */
export function takeTokenFromHash() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('token');
  if (token) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return token;
}
